import type { Asset } from "@/lib/assets";
import type { AssetHazardResult } from "@/lib/results";
import { HORIZONS } from "@shared/hazard-horizons";
import { HAZARD_TYPES, type ClimateScenario, type HazardType, type TimeHorizon } from "@shared/scoring/types";

/** Scenario toggle options: plain-language name plus the SSP code. */
export const SCENARIO_OPTIONS: Array<{ value: ClimateScenario; label: string }> = [
  { value: "SSP2-4.5", label: "Business-as-usual scenario (SSP2-4.5)" },
  { value: "SSP5-8.5", label: "High emission scenario (SSP5-8.5)" },
];

export interface ResultsFilterState {
  hazards: HazardType[];
  countries: string[];
  cities: string[];
  facilities: string[];
  assetImportance: Array<1 | 2 | 3 | 4>;
  valueChainStageIds: string[];
  valueChainImportance: Array<0 | 1 | 2 | 3 | 4>;
  scenario: ClimateScenario;
  horizons: TimeHorizon[];
}

export function defaultResultsFilterState(): ResultsFilterState {
  return {
    hazards: [],
    countries: [],
    cities: [],
    facilities: [],
    assetImportance: [],
    valueChainStageIds: [],
    valueChainImportance: [],
    scenario: "SSP2-4.5",
    horizons: ["historical"],
  };
}

/**
 * Filters the asset list by every asset-scoped filter dimension (an empty
 * array on any dimension means "no filter" there). Hazards/Scenario/Horizons
 * aren't asset fields, so they don't affect this list — they instead scope
 * what's rendered from /api/results downstream (see AssessmentResults.tsx,
 * getResultsForHorizon below).
 *
 * `stageImportanceByStageId` maps a value chain stage id to its workspace
 * rating (0-4, from getWorkspaceValueChainImportance()) — needed for the
 * "Value chain importance" filter, which is a property of the stage, not
 * the asset itself.
 *
 * Pair this with filterResults() below: this narrows *which sites* are in
 * scope, that narrows *which hazard rows* are. Both are needed — filtering
 * only assets leaves every hazard's rows in the data handed to the charts,
 * map, matrix and takeaways.
 */
export function filterAssets(
  assets: Asset[],
  filters: ResultsFilterState,
  stageImportanceByStageId: Record<string, number>,
): Asset[] {
  return assets.filter((asset) => {
    if (filters.countries.length && !filters.countries.includes(asset.country)) return false;
    if (filters.cities.length && !filters.cities.includes(asset.city)) return false;
    if (filters.facilities.length && !filters.facilities.includes(asset.id)) return false;
    if (filters.assetImportance.length && !filters.assetImportance.includes(asset.businessImportance)) return false;

    if (filters.valueChainStageIds.length) {
      if (!asset.valueChainStageId || !filters.valueChainStageIds.includes(asset.valueChainStageId)) return false;
    }

    if (filters.valueChainImportance.length) {
      const importance = asset.valueChainStageId ? stageImportanceByStageId[asset.valueChainStageId] : undefined;
      if (importance === undefined || !filters.valueChainImportance.includes(importance as 0 | 1 | 2 | 3 | 4)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Narrows /api/results to the rows the current filters actually select: the
 * assets that survived filterAssets(), and the hazards chosen in the Hazard
 * filter. An empty `filters.hazards` means "no hazard filter", the same
 * convention every dimension in filterAssets() uses.
 *
 * Scenario and horizon are deliberately NOT applied here — those slice the
 * same rows a second time, per rendered panel, via getResultsForHorizon()
 * (the horizon-comparison grid renders one panel per selected horizon from
 * this same set).
 */
export function filterResults(
  results: AssetHazardResult[],
  filters: ResultsFilterState,
  assetIds: Set<string>,
): AssetHazardResult[] {
  return results.filter((r) => {
    if (!assetIds.has(r.assetId)) return false;
    if (filters.hazards.length && !filters.hazards.includes(r.hazardType)) return false;
    return true;
  });
}

/**
 * The hazards to render, in canonical HAZARD_TYPES order — empty selection
 * means all 6. HazardRiskBarChart and HazardGaugeRow enumerate hazards from
 * a constant rather than from their data (so an unscored hazard still shows
 * a "Pending" slot rather than vanishing), which means filtering the rows
 * alone would leave them rendering empty entries for deselected hazards.
 * Same shape, and same reason, as orderedHorizons() below.
 */
export function orderedHazards(hazards: HazardType[]): HazardType[] {
  return hazards.length === 0 ? [...HAZARD_TYPES] : HAZARD_TYPES.filter((h) => hazards.includes(h));
}

/**
 * Slices a full results set down to one horizon's rows for a given global
 * scenario. `historical` rows have no scenario of their own, so the
 * scenario check is skipped for them. A row with no `scenario` at all (i.e.
 * any `changing_precipitation` row, on any horizon) passes through
 * regardless of the requested scenario, so it never silently drops out of a
 * future-horizon slice the way a strict scenario match would (this exact
 * bug was caught in this feature's spec review).
 */
export function getResultsForHorizon(
  results: AssetHazardResult[],
  horizon: TimeHorizon,
  scenario: ClimateScenario,
): AssetHazardResult[] {
  return results.filter(
    (r) => r.horizon === horizon && (horizon === "historical" || r.scenario === undefined || r.scenario === scenario),
  );
}

/**
 * Re-derives canonical Current→2030→2050 order (and dedupes) from a
 * selection that may be in click order — `ChipToggleGroup`'s onChange
 * appends newly-toggled values to the end of the array, so the Horizon
 * filter's own selection order isn't display order.
 */
export function orderedHorizons(horizons: TimeHorizon[]): TimeHorizon[] {
  return HORIZONS.filter((h) => horizons.includes(h));
}
